import { ConnectedScheduleMenu } from 'components/common-form/sidePanel/ConnectedScheduleMenu';
import { ConnectedDocumentButtonsLegacy } from 'components/common-form/sidePanel/docToolbar/documents-legacy/DocumentButtons.legacy';
import { SmartQuoteWarning } from 'components/common-form/sidePanel/smart-quote-warning';
import { useAppSelector } from 'hooks/useAppSelector';
import { useToggleFeature } from 'hooks/useToggleFeature';
import { getAvailableDocuments } from 'state/selectors';

import { GenericErrorBoundary } from '../builder/form/GenericErrorBoundary';
import { ActionButtons } from './actions/ActionButtons';
import { TradeConfirmation } from './actions/trade/TradeConfirmation';
import { AddonAction } from './addonAction/AddonAction';
import { LegacyDocumentButton } from './docToolbar/documents-legacy';
import { AllDocuments as RegulatoryDocuments } from './docToolbar/regulatory/AllDocuments';
import { AllDocumentsLegacy as RegulatoryDocumentsLegacy } from './docToolbar/regulatory/AllDocuments.legacy';
import { TutorialLink } from './docToolbar/tutorial';
import { QuoteInfos } from './info/QuoteInfos';
import type { MainTabContentProps } from './MainTabContent.connect';
import { connectMainTabContent } from './MainTabContent.connect';
import { PriceEnhancerWithNegotiation } from './priceEnhancer/PriceEnhancerWithNegotiation';
import { ModifiedTabsInfo } from './status/modified-tabs-info';
import { TradeAuthorizationErrors } from './status/quoted.nonTradable/TradeAuthorizationErrors';
import { QuoteWarning } from './status/quoteWarning/quoteWarning.status';
import { ValidationErrors } from './status/validationErrors';
import { TopTabContent } from './topTabs/TopTabContent';

function RawMainTabContent({
  className,
  hasPriceEnhancer,
  hasValidationErrors,
  hasTradeAuthorizationErrors,
  hasTutorialLink,
  hasAddonAction,
  quoteId,
  tabId,
  workspaceType,
  isTrading,
  actions,
  tradeWay,
  requestType,
  modifiedTabCandidates,
}: MainTabContentProps) {
  const isNewDesignEnabled = useToggleFeature('nextDocumentParis');
  const additionalDocumentTypes = useAppSelector(state => getAvailableDocuments(state, workspaceType, tabId));

  return (
    <TopTabContent
      className={className}
      header={
        <div id="side-panel-header" data-e2e="side-panel-header" className="d-flex flex-column gap-12px">
          {hasTradeAuthorizationErrors && <TradeAuthorizationErrors tabId={tabId} workspaceType={workspaceType} />}
          {quoteId && <QuoteWarning quoteId={quoteId} />}
          {quoteId && <SmartQuoteWarning formId={tabId} />}
          {modifiedTabCandidates.length > 0 && <ModifiedTabsInfo candidates={modifiedTabCandidates} />}
          {hasValidationErrors && (
            <GenericErrorBoundary>
              <ValidationErrors
                tabId={tabId}
                workspaceType={workspaceType}
                onlyErrorsDisplayedInSummary
                errorPrefix="quote.errorMessages."
              />
            </GenericErrorBoundary>
          )}
          {hasTutorialLink && <TutorialLink tabId={tabId} workspaceType={workspaceType} />}

          {hasAddonAction && <AddonAction tabId={tabId} workspaceType={workspaceType} />}
          {hasPriceEnhancer && (
            <GenericErrorBoundary>
              <PriceEnhancerWithNegotiation tabId={tabId} quoteId={quoteId!} workspaceType={workspaceType} />
            </GenericErrorBoundary>
          )}
        </div>
      }
      body={
        <GenericErrorBoundary>
          {isTrading ? (
            <TradeConfirmation
              className="h-100 overflow-y-auto"
              tabId={tabId}
              workspaceType={workspaceType}
              termsheetButton={
                <LegacyDocumentButton
                  tabId={tabId}
                  workspaceType={workspaceType}
                  className="mt-3"
                  documentType="TermSheet"
                />
              }
            />
          ) : (
            <QuoteInfos tabId={tabId} workspaceType={workspaceType} />
          )}
        </GenericErrorBoundary>
      }
      footer={
        <>
          <div>
            {!isNewDesignEnabled && (
              <div className="mb-2">
                <ConnectedDocumentButtonsLegacy tabId={tabId} workspaceType={workspaceType} />
              </div>
            )}
            {isNewDesignEnabled ? (
              <RegulatoryDocuments
                tabId={tabId}
                workspaceType={workspaceType}
                additionnalDocumentTypes={additionalDocumentTypes}
              />
            ) : (
              <RegulatoryDocumentsLegacy tabId={tabId} workspaceType={workspaceType} />
            )}
          </div>
          <ActionButtons
            tabId={tabId}
            workspaceType={workspaceType}
            quoteId={quoteId}
            type={actions}
            tradeWay={tradeWay}
            requestType={requestType}
          >
            <ConnectedScheduleMenu
              analyticContext={workspaceType}
              workspaceType={workspaceType}
              id={tabId}
              wrapperClassName="d-flex justify-content-end w-100"
              btnClassName="btn btn-flat-secondary btn-icon-end"
            />
          </ActionButtons>
        </>
      }
    />
  );
}

export const MainTabContent = connectMainTabContent(RawMainTabContent);
